import { Box, Button, Divider, Flex, Stack, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EvaluationCriteriaItemCard } from '@/module/Evaluation/EvaluationCriteriaItemCard';
import { EvaluationCriteriaItemCreate } from '@/module/Evaluation/EvaluationCriteriaItemCreate';
import { EvaluationCriteriaItemEdit } from '@/module/Evaluation/EvaluationCriteriaItemEdit';
import { EvaluationItemValidationAction } from '@/module/Evaluation/Validation/EvaluationItemValidationAction';
import { EvaluationItemValidationHistory } from '@/module/Evaluation/Validation/EvaluationItemValidationHistory';
import {
  getEvaluationCriteriaDataOptions,
  useEvaluationRecalculateCriteriaItem,
} from '@/module/Evaluation/model/EvaluationQuery';
import {
  EvaluationCriteria,
  EvaluationCriteriaDataItem,
  isEvaluationCriteriaAbstractItem,
  isEvaluationCriteriaArticleItem,
  isEvaluationCriteriaBookItem,
  isEvaluationCriteriaBooleanItem,
  isEvaluationCriteriaChapterItem,
  isEvaluationCriteriaCommitteeItem,
  isEvaluationCriteriaCourseItem,
  isEvaluationCriteriaDoctoralCommitteeItem,
  isEvaluationCriteriaEditorialTeamItem,
  isEvaluationCriteriaFreeItem,
  isEvaluationCriteriaNumberItem,
  isEvaluationCriteriaPatentItem,
  isEvaluationCriteriaProceedingItem,
  isEvaluationCriteriaProjectItem,
  isEvaluationCriteriaTranslationItem,
} from '@/module/Evaluation/model/EvaluationTypes';

import { Content, NoContentPlaceholder } from '@/core/components';

import { Id, assertUnreachable, useAppNotification } from '@/feature/utils';

interface EvaluationCriteriaGatheringProps {
  criteria: EvaluationCriteria;
  evaluationId: Id;
  authorId: Id;

  isEvaluationClosed: boolean;
}

function extractSelectId(item: EvaluationCriteriaDataItem): number | null {
  if (isEvaluationCriteriaNumberItem(item)) {
    return null;
  }

  if (isEvaluationCriteriaBooleanItem(item)) {
    return null;
  }

  if (isEvaluationCriteriaFreeItem(item)) {
    return null;
  }

  if (isEvaluationCriteriaProceedingItem(item)) {
    return item.proceeding.id;
  }

  if (isEvaluationCriteriaArticleItem(item)) {
    return item.article.id;
  }

  if (isEvaluationCriteriaCourseItem(item)) {
    return item.course.id;
  }

  if (isEvaluationCriteriaAbstractItem(item)) {
    return item.abstractItem.id;
  }

  if (isEvaluationCriteriaBookItem(item)) {
    return item.book.id;
  }

  if (isEvaluationCriteriaChapterItem(item)) {
    return item.chapter.id;
  }

  if (isEvaluationCriteriaCommitteeItem(item)) {
    return item.committee.id;
  }

  if (isEvaluationCriteriaPatentItem(item)) {
    return item.patent.id;
  }

  if (isEvaluationCriteriaProjectItem(item)) {
    return item.project.id;
  }

  if (isEvaluationCriteriaTranslationItem(item)) {
    return item.translation.id;
  }

  if (isEvaluationCriteriaEditorialTeamItem(item)) {
    return item.editorialTeam.id;
  }

  if (isEvaluationCriteriaDoctoralCommitteeItem(item)) {
    return item.committee.id;
  }

  assertUnreachable(item);
}

export function EvaluationCriteriaGathering({
  evaluationId,
  criteria,
  authorId,
  isEvaluationClosed,
}: EvaluationCriteriaGatheringProps) {
  const { data, refetch } = useSuspenseQuery(
    getEvaluationCriteriaDataOptions(evaluationId, authorId, criteria.orderNo),
  );
  const [isAdding, { open: openAdd, close: closeAdd }] = useDisclosure(false);

  const [editItem, setEditItem] = useState<EvaluationCriteriaDataItem | null>(null);
  const closeEdit = () => setEditItem(null);
  const isEditing = editItem != null;

  const {
    criteria: { discriminatorList },
    data: { items, authorPoints, headOfDepartmentPoints, headOfDisciplinePoints },
  } = data;
  const alreadySelectedIds = useMemo(() => items.map(extractSelectId).filter(id => id != null), [items]);
  const { t } = useTranslation();

  const notification = useAppNotification();

  const { mutate } = useEvaluationRecalculateCriteriaItem(evaluationId, authorId, criteria.orderNo);
  const onRecalculate = (item: EvaluationCriteriaDataItem) => {
    mutate(item.id, {
      onSuccess: notification.success,
      onError: notification.error,
    });
  };

  if (isEditing) {
    return (
      <EvaluationCriteriaItemEdit
        editItem={editItem}
        close={closeEdit}
        discriminatorList={discriminatorList}
        evaluationId={evaluationId}
        authorId={authorId}
        orderNo={criteria.orderNo}
        criteriaType={criteria.criteriaType}
        dataEntryHint={criteria.dataEntryHint}
        alreadySelectedIds={alreadySelectedIds}
      />
    );
  }

  if (isAdding) {
    return (
      <EvaluationCriteriaItemCreate
        close={closeAdd}
        discriminatorList={discriminatorList}
        evaluationId={evaluationId}
        authorId={authorId}
        orderNo={criteria.orderNo}
        criteriaType={criteria.criteriaType}
        dataEntryHint={criteria.dataEntryHint}
        alreadySelectedIds={alreadySelectedIds}
      />
    );
  }

  return (
    <Box
      p="sm"
      component={Stack}
    >
      <Flex
        align="center"
        justify="space-between"
      >
        <Stack gap="2px">
          <Text
            size="sm"
            fw="500"
          >
            Total punctaj criteriu: {authorPoints}
          </Text>
          {isEvaluationClosed && (
            <Text
              size="sm"
              fw="500"
            >
              Total punctaj criteriu șef de disciplină: {headOfDisciplinePoints}
            </Text>
          )}
          {isEvaluationClosed && (
            <Text
              size="sm"
              fw="500"
            >
              Total punctaj criteriu director departament: {headOfDepartmentPoints}
            </Text>
          )}
        </Stack>
        <Button
          size="xs"
          onClick={openAdd}
        >
          {t('button.add')}
        </Button>
      </Flex>
      <Text size="sm">{t('evaluation.pointsDisclaimer')}</Text>
      <Divider />
      {items.length === 0 && <NoContentPlaceholder title={t('evaluation.noCriteriaItems')} />}
      <Stack gap="xl">
        {items.length > 0 &&
          items.map((item, index) => (
            <Stack
              component={Content}
              gap="3px"
              key={item.id}
            >
              <Flex gap="3px">
                {isEvaluationClosed && (
                  <EvaluationItemValidationAction
                    variant="compact"
                    itemId={item.id}
                    onAfterValidation={refetch}
                  />
                )}
                <EvaluationCriteriaItemCard
                  index={index}
                  item={item}
                  evaluationId={evaluationId}
                  authorId={authorId}
                  orderNo={criteria.orderNo}
                  dataEntryHint={criteria.dataEntryHint}
                  onEdit={setEditItem}
                  onRecalculate={onRecalculate}
                />
              </Flex>
              {isEvaluationClosed && (
                <EvaluationItemValidationHistory
                  defaultIsCollapsed
                  itemId={item.id}
                />
              )}
            </Stack>
          ))}
      </Stack>
    </Box>
  );
}
